<svelte:options runes={false} />
<script>
  // Savings waterfall (TASK-20260823-twap-strategies-tca AC 10): two SVG series side by side —
  // "net savings vs sweep" (price improvement → fees saved → = net) and "implementation shortfall"
  // (execution cost vs arrival → opportunity cost → = IS) — with market drift printed as context
  // under them, never as a bar in either. Sign is encoded by bar direction AND the signed label,
  // so gain/loss is never color-alone. A null step renders as a hatched placeholder with its note.
  import { linearScale, niceDomain, formatTick } from '../charts/svgScales.js';
  import { fmtQuote } from '../../../lib/twapReport.js';

  export let waterfall = null;   // from savingsWaterfall(scorecard)
  export let currency = 'USD';

  const H = 180;
  const PAD = { top: 14, right: 12, bottom: 42, left: 52 };
  let width = 320;

  function layout(series, w) {
    const values = series.flatMap((s) => [s.start, s.end]).filter((v) => typeof v === 'number');
    const [lo, hi] = values.length ? niceDomain(Math.min(0, ...values), Math.max(0, ...values), 4) : [0, 1];
    const y = linearScale([lo, hi], [H - PAD.bottom, PAD.top]);
    const plotW = Math.max(60, w - PAD.left - PAD.right);
    const slot = plotW / series.length;
    const barW = Math.min(64, slot * 0.6);
    return {
      y,
      ticks: y.ticks(4),
      bars: series.map((s, i) => {
        const x = PAD.left + slot * i + (slot - barW) / 2;
        const hasValue = s.value !== null && s.start !== null && s.end !== null;
        const top = hasValue ? Math.min(y(s.start), y(s.end)) : PAD.top;
        const height = hasValue ? Math.max(1, Math.abs(y(s.end) - y(s.start))) : H - PAD.bottom - PAD.top;
        return { ...s, x, barW, top, height, hasValue, cx: x + barW / 2 };
      }),
      zero: y(0),
    };
  }

  $: savings = waterfall ? layout(waterfall.savings, width) : null;
  $: shortfall = waterfall ? layout(waterfall.shortfall, width) : null;
  $: drift = waterfall?.drift ?? null;
  $: notes = waterfall?.notes ?? [];

  function fillClass(bar) {
    if (!bar.hasValue) return 'wf-missing';
    if (bar.isTotal) return bar.value >= 0 ? 'wf-total-gain' : 'wf-total-loss';
    return bar.value >= 0 ? 'wf-gain' : 'wf-loss';
  }
</script>

<div class="wf" bind:clientWidth={width}>
  {#if !waterfall}
    <p class="wf-empty">No scorecard yet.</p>
  {:else}
    <div class="wf-pair">
      {#each [['Net savings vs sweep', savings, 'price improvement + fees saved'], ['Implementation shortfall', shortfall, 'execution cost + opportunity cost']] as [title, chart, sub]}
        <figure class="wf-fig">
          <figcaption class="wf-title">{title} <span class="wf-sub">{sub}</span></figcaption>
          <svg class="wf-svg" viewBox={`0 0 ${Math.max(200, width / 2 - 8)} ${H}`} width="100%" height={H} role="img"
            aria-label={`${title}: ${chart.bars.map((b) => `${b.label} ${b.hasValue ? fmtQuote(b.value, currency) : 'unavailable'}`).join('; ')}`}>
            <defs>
              <pattern id="wf-hatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                <line x1="0" y1="0" x2="0" y2="6" class="wf-hatch-line" />
              </pattern>
            </defs>
            {#each chart.ticks as t}
              <line x1={PAD.left} x2={Math.max(200, width / 2 - 8) - PAD.right} y1={chart.y(t)} y2={chart.y(t)} class="wf-grid" />
              <text x={PAD.left - 6} y={chart.y(t) + 3} class="wf-tick" text-anchor="end">{formatTick(t)}</text>
            {/each}
            <line x1={PAD.left} x2={Math.max(200, width / 2 - 8) - PAD.right} y1={chart.zero} y2={chart.zero} class="wf-zero" />
            {#each chart.bars as bar, i}
              <g>
                <rect x={bar.x} y={bar.top} width={bar.barW} height={bar.height} class={fillClass(bar)} rx="2"
                  fill={bar.hasValue ? undefined : 'url(#wf-hatch)'}>
                  <title>{bar.label}: {bar.hasValue ? fmtQuote(bar.value, currency) : 'unavailable'}</title>
                </rect>
                {#if i > 0 && bar.hasValue && chart.bars[i - 1].hasValue && !bar.isTotal}
                  <line x1={chart.bars[i - 1].x + chart.bars[i - 1].barW} x2={bar.x} y1={chart.y(bar.start)} y2={chart.y(bar.start)} class="wf-connector" />
                {/if}
                <text x={bar.cx} y={bar.hasValue ? bar.top - 4 : PAD.top + 12} class="wf-value" text-anchor="middle">
                  {bar.hasValue ? fmtQuote(bar.value, currency) : '—'}
                </text>
                <text x={bar.cx} y={H - PAD.bottom + 14} class="wf-label" text-anchor="middle">{bar.isTotal ? '= ' : ''}{bar.label.split(' (')[0].split(' vs ')[0]}</text>
              </g>
            {/each}
          </svg>
        </figure>
      {/each}
    </div>
    {#if !waterfall.reconciles && waterfall.complete}
      <p class="wf-warn" role="status">⚠ The server's net savings does not equal price improvement + fees saved — shown as reported, not corrected.</p>
    {/if}
    <p class="wf-drift">
      Market drift (context, not a saving):
      {#if drift}
        <strong class={drift.against ? 'wf-loss-text' : 'wf-gain-text'}>
          {Math.abs(drift.bps).toFixed(1)} bps {drift.against ? 'against' : 'in favour of'} the order {drift.against ? '▼' : '▲'}
        </strong>
        {#if drift.quote !== null}<span class="wf-sub">({fmtQuote(drift.quote, currency, { signed: false })} on the order size over the window)</span>{/if}
      {:else}
        <span>—</span>
      {/if}
    </p>
    {#if notes.length}
      <ul class="wf-notes">{#each notes as n}<li>{n}</li>{/each}</ul>
    {/if}
  {/if}
</div>

<style>
  .wf { width: 100%; min-width: 0; }
  .wf-pair { display: grid; grid-template-columns: 1fr 1fr; gap: var(--spacing-md, 0.75rem); }
  @media (max-width: 640px) { .wf-pair { grid-template-columns: 1fr; } }
  .wf-fig { margin: 0; min-width: 0; }
  .wf-title { font-size: var(--font-size-sm, 0.85rem); color: var(--text-secondary, #8a8b8d); margin-bottom: 0.25rem; }
  .wf-sub { font-size: var(--font-size-xs, 0.7rem); color: var(--text-muted, #6a6b6d); }
  .wf-svg { display: block; border: 1px solid var(--border-primary, #1e1f22); border-radius: var(--radius-base, 0.5rem); background: var(--bg-secondary, #111214); }
  .wf-grid { stroke: var(--chart-grid, #1e1f22); stroke-width: 1; }
  .wf-zero { stroke: var(--text-muted, #6a6b6d); stroke-width: 1; }
  .wf-tick, .wf-label, .wf-value { fill: var(--text-secondary, #8a8b8d); font-size: 10px; font-variant-numeric: tabular-nums; }
  .wf-value { fill: var(--text-primary, #fff); font-weight: 600; }
  .wf-gain { fill: var(--color-success, #00ff88); opacity: 0.75; }
  .wf-loss { fill: var(--color-danger, #ff3b3b); opacity: 0.75; }
  .wf-total-gain { fill: var(--color-success, #00ff88); }
  .wf-total-loss { fill: var(--color-danger, #ff3b3b); }
  .wf-missing { stroke: var(--border-tertiary, #3a3b3e); stroke-dasharray: 3 3; }
  .wf-hatch-line { stroke: var(--border-tertiary, #3a3b3e); stroke-width: 1; }
  .wf-connector { stroke: var(--text-muted, #6a6b6d); stroke-dasharray: 2 2; }
  .wf-drift { margin: 0.5rem 0 0; font-size: var(--font-size-sm, 0.85rem); color: var(--text-secondary, #8a8b8d); font-variant-numeric: tabular-nums; }
  .wf-loss-text { color: var(--color-danger, #ff3b3b); }
  .wf-gain-text { color: var(--color-success, #00ff88); }
  .wf-warn { margin: 0.4rem 0 0; font-size: var(--font-size-xs, 0.7rem); color: var(--color-warning, #ffaa00); }
  .wf-notes { margin: 0.4rem 0 0; padding-left: 1.1rem; font-size: var(--font-size-xs, 0.7rem); color: var(--text-muted, #6a6b6d); }
  .wf-empty { color: var(--text-muted, #6a6b6d); font-size: var(--font-size-sm, 0.85rem); }
</style>
